import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { palettes, THEMES, toCssVariables } from './tokens';

const STORAGE_KEY = 'wm-theme';
const MODES = ['system', ...THEMES];

const ThemeContext = createContext(null);

const getSystemTheme = () =>
  window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const readMode = () => {
  const saved = localStorage.getItem(STORAGE_KEY);
  return MODES.includes(saved) ? saved : 'system';
};

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(readMode);
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return undefined;
    const onChange = () => setSystemTheme(getSystemTheme());
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const theme = mode === 'system' ? systemTheme : mode;

  useLayoutEffect(() => {
    const root = document.documentElement;
    Object.entries(toCssVariables(theme)).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    root.dataset.theme = theme;
    root.style.colorScheme = palettes[theme].isDark ? 'dark' : 'light';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', palettes[theme].bg);
  }, [theme]);

  const setMode = useCallback(next => {
    const root = document.documentElement;
    root.classList.add('theme-transition');
    window.setTimeout(() => root.classList.remove('theme-transition'), 320);
    localStorage.setItem(STORAGE_KEY, next);
    setModeState(next);
  }, []);

  const value = useMemo(
    () => ({ mode, theme, setMode, palette: palettes[theme], modes: MODES }),
    [mode, theme, setMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
